import React, { useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  useTheme,
} from "@material-ui/core";
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  LocalOffer as DiscountIcon,
} from "@material-ui/icons";
import { usePdvStyles } from "../styles/pdvStyles";
import clsx from "clsx";

const formatCurrency = (value) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value || 0);
};

const PdvCartItem = ({
  item,
  index,
  isSelected = false,
  onUpdateQuantity,
  onRemove,
  onApplyDiscount,
  onClick,
}) => {
  const classes = usePdvStyles();
  const theme = useTheme();

  const originalPrice = item.originalPrice || item.unitPrice;
  const hasDiscount = item.discountValue > 0 || item.discountPercent > 0;
  const itemTotal = item.unitPrice * item.quantity;

  const handleIncrease = useCallback(
    (e) => {
      e.stopPropagation();
      onUpdateQuantity(index, item.quantity + 1);
    },
    [index, item.quantity, onUpdateQuantity]
  );

  const handleDecrease = useCallback(
    (e) => {
      e.stopPropagation();
      if (item.quantity > 1) {
        onUpdateQuantity(index, item.quantity - 1);
      }
    },
    [index, item.quantity, onUpdateQuantity]
  );

  const handleRemove = useCallback(
    (e) => {
      e.stopPropagation();
      onRemove(index);
    },
    [index, onRemove]
  );

  const handleApplyDiscount = useCallback(
    (e) => {
      e.stopPropagation();
      onApplyDiscount(index, item);
    },
    [index, item, onApplyDiscount]
  );

  const handleClick = useCallback(() => {
    if (onClick) {
      onClick(index);
    }
  }, [index, onClick]);

  return (
    <Box
      className={clsx(classes.productItem, {
        [classes.productItemSelected]: isSelected,
      })}
      onClick={handleClick}
      data-cart-index={index}
    >
      {/* Left side: Product info (same as PdvProductItem) */}
      <Box className={classes.productItemInfo}>
        {/* Linha 1: Nome do produto */}
        <Typography className={classes.productName} title={item.name}>
          {item.name}
        </Typography>

        {/* Linha 2: Principio ativo (if available) */}
        {item.activePrinciple && (
          <Typography className={classes.productActivePrinciple} title={item.activePrinciple}>
            {item.activePrinciple}
          </Typography>
        )}

        {/* Linha 3: Preco unitario e desconto */}
        <Box className={classes.productDetails}>
          <span className={classes.productBarcode}>
            {item.quantity}x {formatCurrency(item.unitPrice)}
          </span>
          {hasDiscount && (
            <span style={{ color: theme.palette.success.main, display: 'flex', alignItems: 'center', gap: 2 }}>
              <DiscountIcon style={{ fontSize: 12 }} />
              -{item.discountPercent?.toFixed(0)}%
            </span>
          )}
          {hasDiscount && (
            <span style={{ color: theme.palette.text.disabled, textDecoration: 'line-through', fontSize: '0.7rem' }}>
              {formatCurrency(originalPrice)}
            </span>
          )}
        </Box>
      </Box>

      {/* Right side: Price and controls (same layout as PdvProductItem) */}
      <Box className={classes.productPriceSection}>
        {/* Total do item */}
        <Typography className={classes.productPrice}>
          {formatCurrency(itemTotal)}
        </Typography>

        {/* Quantity Controls + Action Buttons */}
        <Box className={classes.productPriceControls}>
          {/* Quantity controls */}
          <Box className={classes.productQuantityControls}>
            <Tooltip title="Diminuir">
              <span>
                <IconButton
                  className={classes.productQtyButton}
                  onClick={handleDecrease}
                  disabled={item.quantity <= 1}
                  size="small"
                >
                  <RemoveIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Typography className={classes.productQtyDisplay}>
              {item.quantity}
            </Typography>
            <Tooltip title="Aumentar">
              <span>
                <IconButton
                  className={classes.productQtyButton}
                  onClick={handleIncrease}
                  size="small"
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          {/* Discount Button */}
          <Tooltip title={hasDiscount ? "Alterar desconto (F2)" : "Aplicar desconto (F2)"}>
            <span>
              <IconButton
                className={classes.productAddButton}
                onClick={handleApplyDiscount}
                size="small"
                style={{
                  backgroundColor: hasDiscount ? theme.palette.success.main : undefined,
                }}
              >
                <DiscountIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          {/* Remove Button */}
          <Tooltip title="Remover (Delete)">
            <span>
              <IconButton
                onClick={handleRemove}
                size="small"
                style={{
                  backgroundColor: theme.palette.error.main,
                  color: theme.palette.error.contrastText,
                  minWidth: 30,
                  width: 30,
                  height: 30,
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

export default React.memo(PdvCartItem);
